import { useEffect } from 'react';
import classNames from 'classnames';
import { FieldValues, Path, RegisterOptions, useFormContext } from 'react-hook-form';
import InputField, { InputProps } from '../InputField/InputField';
import './FormInput.scss';
import { addHyphenPhoneNum } from '../../../lib/utils';

export type FormInputProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  rules?: RegisterOptions;
  label: string;
  value?: string;
  editable: boolean;
  required: boolean;
  limit?: number;
} & Omit<InputProps, 'name'>;

export const FormInput = <TFormValues extends Record<string, unknown>>({
  name,
  rules,
  className,
  value,
  editable = true,
  required = false,
  limit,
  ...props
}: FormInputProps<TFormValues>): JSX.Element => {
  const { register, formState, watch, setValue } = useFormContext();
  const { errors } = formState;
  const hasError = !!errors?.[name];
  const errorMessage = errors?.[name]?.message;
  const errorText = typeof errorMessage === 'string' ? errorMessage : null;

  const watchedValue = watch(name);

  useEffect(() => {
    if (name === 'phone') {
      setValue(name, addHyphenPhoneNum(watchedValue));
    }
  }, [watchedValue, name, setValue]);

  const fieldType = name === 'password' || name === 'passwordConfirm' ? 'password' : props.type;

  return (
    <div className={classNames('form-input', className)} aria-live="polite">
      <InputField
        {...props}
        className={classNames()}
        value={value}
        hasError={hasError}
        {...(register && register(name, { ...rules, required, maxLength: limit }))}
        readOnly={!editable}
        type={fieldType}
      />
      <p className="error-message" data-testid={`${name}-error`}>
        {hasError ? (watchedValue?.length ? errorText : '필수 정보입니다.') : null}
      </p>
    </div>
  );
};
